import { debug, Dropdown, Icon, LightGrid, plugin, useLightContext } from 'light-component';
import { useEffect, useMemo, useState, useCallback, type ReactElement } from 'react';
import { Modal, Select } from 'antd';
import { Icons } from 'zf-web-components/icons';
import { useTranslation } from '../../../i18n';
import styles from '../style.module.less';
import { ValueComponent } from './value-component';
import type { LightContext, ShowLogicProps, ConditionItem } from '../types';

export function WidgetShowLogic(props: ShowLogicProps): ReactElement {
  debug.widget('form show logic', props);

  const [t] = useTranslation();

  const CONDITION_OPTIONS = [
    { value: 'or', label: t('任一') },
    { value: 'and', label: t('所有') }
  ];

  const UNSUPPORTED_TYPES = [
    'Block',
    'ShotEyesGrid',
    'ShotEyesSignature',
    'ShotEyesImage',
    'RichText',
    'Input',
    'Calendar',
    'TextArea'
  ];

  const CONDITION_MAP = {
    RadioBox: [
      { value: 'eq', title: t('等于') },
      { value: 'ne', title: t('不等于') },
      { value: 'empty', title: t('为空') },
      { value: 'notEmpty', title: t('不为空') }
    ],
    CheckBox: [
      { value: 'eq', title: t('等于') },
      { value: 'ne', title: t('不等于') },
      { value: 'empty', title: t('为空') },
      { value: 'notEmpty', title: t('不为空') }
    ],
    ShotEyesStar: [
      { value: 'eq', title: t('等于') },
      { value: 'ne', title: t('不等于') },
      { value: 'gt', title: t('大于') },
      { value: 'gte', title: t('大于或等于') },
      { value: 'lt', title: t('小于') },
      { value: 'lte', title: t('小于或等于') },
      { value: 'empty', title: t('为空') },
      { value: 'notEmpty', title: t('不为空') }
    ],
    Number: [
      { value: 'eq', title: t('等于') },
      { value: 'ne', title: t('不等于') },
      { value: 'gt', title: t('大于') },
      { value: 'gte', title: t('大于或等于') },
      { value: 'lt', title: t('小于') },
      { value: 'lte', title: t('小于或等于') },
      { value: 'empty', title: t('为空') },
      { value: 'notEmpty', title: t('不为空') }
    ]
  };
  const context = useLightContext() as LightContext;
  const [options, setOptions] = useState<ConditionItem[]>(props.value?.options ?? []);
  const [modalOptions, setModalOptions] = useState<ConditionItem[]>(props.value?.options ?? []);
  const [open, setOpen] = useState<boolean>(false);

  // 使用 useMemo 优化 items 计算
  const items = useMemo(() => {
    // 只找同分组下的题目
    const item = context.source.items.find(item => item.name === context.active);
    // 目前只有一层分组，所以只取 path[0]
    const items = context.source.items.filter(({ path }) => path[0] === item!.path[0]);
    const index = items.findIndex(item => item.name === context.active);

    return items
      .slice(0, index)
      .map(item => ({
        value: item.name,
        title: item.title,
        type: item.type,
        options: item.option ?? []
      }))
      .filter(item => !UNSUPPORTED_TYPES.includes(item.type));
  }, [context.active, context.source.items]);

  useEffect(() => {
    setOptions(props.value?.options ?? []);
  }, [context.active, props.value?.options]);

  const onConditionChange = useCallback((value: ConditionItem[]): void => {
    const data = open ? modalOptions : options;
    const changedConditions = value.filter(newItem => !data.some(existingItem => existingItem.item === newItem.item
        && existingItem.index === newItem.index));
    if (changedConditions[0] != null) {
      changedConditions[0].condition = '';
      changedConditions[0].value = '';
    }

    if (open) {
      setModalOptions([...value]);
    } else {
      setOptions([...value]);
      props.onChange?.({ condition: props.value?.condition ?? 'and', options: value });
    }
  }, [open, modalOptions, options]);

  const onModalOpen = useCallback((): void => {
    setModalOptions(options);
    setOpen(true);
  }, [options]);

  const onModalClose = useCallback((ok: boolean): void => {
    if (ok) {
      setOptions(modalOptions);
      props.onChange?.({ condition: props.value?.condition ?? 'and', options: modalOptions });
    }
    setOpen(false);
  }, [modalOptions]);

  const onConditionItemChange = useCallback((value: ConditionItem): void => {
    const data = open ? modalOptions : options;
    const item = data.find(item => item.index === value.index);
    if (item === undefined) {
      return;
    }

    item.value = value.value;
    item.condition = value.condition;
    if (open) {
      setModalOptions([...data]);
    } else {
      setOptions([...data]);
      props.onChange?.({ condition: props.value?.condition ?? 'and', options: data });
    }
  }, [open, modalOptions, options]);

  const renderCondition = useCallback((conditionItem: ConditionItem): ReactElement => {
    const item = items.find(item => item.value === conditionItem.item);
    const conditions = item != null ? CONDITION_MAP[item.type as keyof typeof CONDITION_MAP] : [];

    return (
      <Dropdown
        value={conditionItem.condition}
        placeholder={t('条件')}
        icon={<Icon name='Down' />}
        options={conditions}
        placement='bottomLeft'
        origin='grid'
        onSelect={value => onConditionItemChange({ ...conditionItem, condition: value })}
      />
    );
  }, [items, onConditionItemChange]);

  const gridOptions = useMemo(() => [
    {
      type: 'select' as const,
      name: 'item',
      title: t('题目'),
      width: open ? 200 : 70,
      options: items,
      showInput: false,
      placeholder: t('点此选择')
    },
    {
      type: 'dynamic' as const,
      name: 'condition',
      title: t('条件'),
      width: open ? 120 : 50,
      render: (value: unknown): ReactElement => renderCondition(value as ConditionItem)
    },
    {
      type: 'dynamic' as const,
      name: 'value',
      title: t('取值'),
      width: open ? 200 : 60,
      render: (value: unknown): ReactElement => (
        <ValueComponent
          width={open ? 180 : 60}
          conditionItem={value as ConditionItem}
          item={items.find(item => item.value === (value as ConditionItem).item)}
          onChange={onConditionItemChange}
        />
      )
    }
  ], [items, open, renderCondition]);

  return (
    <div className={props.className ?? styles.propertyItem}>
      <div className={styles.input}>
        <span className={styles.title}>{props.title}</span>
        <Icons
          name='ExpandAltOutlined'
          className={styles.floatRight}
          onClick={onModalOpen}
        />
      </div>
      <div className={styles.showLogicDescription}>
        {t('当前题目满足以下')}
        <Select
          size='small'
          style={{ width: 74, padding: '0 4px' }}
          options={CONDITION_OPTIONS}
          value={props.value?.condition ?? 'and'}
          onChange={value => props.onChange?.({ ...props.value, condition: value })}
        />
        {t('条件则显示')}
      </div>
      <div className={styles.option}>
        <LightGrid
          keyField='index'
          data={options}
          onChange={onConditionChange}
          options={gridOptions}
          dragAlign='none'
        />
      </div>

      <Modal
        centered
        title={props.title}
        open={open}
        cancelText={t('取消')}
        okText={t('保存')}
        destroyOnClose
        onOk={() => onModalClose(true)}
        onCancel={() => onModalClose(false)}
        closable={false}
        okButtonProps={{ autoInsertSpace: false }}
        cancelButtonProps={{ autoInsertSpace: false }}
        width={840}
      >
        <LightGrid
          keyField='index'
          data={modalOptions}
          onChange={onConditionChange}
          options={gridOptions}
          dragAlign='none'
        />
      </Modal>
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'showlogic', component: WidgetShowLogic });
